import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users, Loader2 } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { useApp } from "@/lib/app-context";

export const JOIN_KEY = "ww_family_join";

export default function FamilyJoin() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const { me, openSignup } = useApp();
  const api = useApi();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (token) { localStorage.setItem(JOIN_KEY, token); }
  }, [token]);
  useEffect(() => {
    if (isSignedIn && me && token) {
      localStorage.removeItem(JOIN_KEY);
      api("get", "/family/invites/lookup", undefined, { params: { token } }).then(setInfo).catch((e) => setError(errMsg(e)));
    }
  }, [api, isSignedIn, me, token]);

  const accept = async () => {
    setBusy(true);
    try {
      const r = await api("post", "/family/invites/accept", { token });
      localStorage.removeItem(JOIN_KEY);
      toast.success(`You can now view ${r.owner_name}'s wallet`);
      navigate(`/shared/${r.owner_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  let body;
  if (!token) body = <p className="text-ww-muted" data-testid="family-join-error">This invitation link is incomplete.</p>;
  else if (!isLoaded || (isSignedIn && !info && !error)) body = <Loader2 className="mx-auto h-6 w-6 animate-spin text-ww-emerald" />;
  else if (!isSignedIn) body = (<><p className="text-ww-muted">Sign in or create a free account with the email address the invitation was sent to.</p>
    <button onClick={openSignup} className="ww-btn-primary mt-6" data-testid="family-join-signin">Sign In or Create Account</button></>);
  else if (error) body = <p className="text-red-600" data-testid="family-join-error">{error}</p>;
  else if (info.own) body = <p className="text-ww-muted" data-testid="family-join-error">This is your own invitation. Share it with your family member.</p>;
  else body = (<><p className="text-ww-muted"><strong className="text-ww-navy">{info.owner_name}</strong> invited <strong className="text-ww-navy">{info.email}</strong> to view their household wallet and its reminders.</p>
    {info.matches ? <button onClick={accept} disabled={busy} className="ww-btn-primary mt-6" data-testid="family-join-accept">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Accept Invitation</button>
      : <p className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800" data-testid="family-join-mismatch">You're signed in as {me.email || "an account without a verified email"}. Sign in with {info.email} to accept.</p>}
    {!info.matches && <button onClick={() => { localStorage.setItem(JOIN_KEY, token); signOut({ redirectUrl: `/family/join?token=${encodeURIComponent(token)}` }); }} className="ww-btn-ghost mt-4" data-testid="family-join-switch-account">Sign Out and Switch Account</button>}</>);

  return (
    <main className="mx-auto max-w-lg px-5 py-20 text-center" data-testid="family-join-page">
      <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><Users className="h-8 w-8" /></span>
      <h1 className="mt-6 font-display text-3xl font-semibold text-ww-navy">Household Wallet Invitation</h1>
      <div className="mt-4">{body}</div>
    </main>
  );
}
